// API документов и контроля среза 003 (data-model.md, «Матрица операций»; contracts/api.md 003).
// Записи в таблицу маршрутов, матрицу прав и каталоги добавляет дорожка C вместе с сервером
// (plan.md, «Дорожки»): маршрут без обработчика роняет проверку OpenAPI и тесты прав.
import { z } from '../kernel/zod.ts';
import {
  DateSchema,
  IdSchema,
  OperationIdSchema,
  PersonRefSchema,
  TimestampSchema,
} from '../kernel/common.ts';
import { ErrorCodeSchema } from '../errors/catalog.ts';
import { ClarifyReasonSchema, ItemKindSchema, ResponsibleSourceSchema } from '../protocol/item.ts';
import { ClosedBySchema, ItemStatusSchema } from '../protocol/status.ts';
import { AddresseeSchema } from '../directory/addressee.ts';
import { ControlResponsibleSchema } from './directory.ts';
import { DocumentModeSchema } from '../minutes/protocol.ts';

/**
 * Тело загрузки — файл как есть с типом XLSX_CONTENT_TYPE (api/audit.ts).
 * Предел файла протокола (spec.md, «Допущения»).
 */
export const PROTOCOL_FILE_MAX_BYTES = 20 * 1024 * 1024;
/** Имя файла — в заголовке, закодированным encodeURIComponent: тело занято файлом. */
export const FILE_NAME_HEADER = 'x-kp-file-name';

/** Заголовки загрузки: имя файла (дорожка C, маршруты documents.create и versions.create). */
export const UploadHeadersSchema = z.object({ [FILE_NAME_HEADER]: z.string().min(1).max(2048) });

/** Документ и его версия в пути: оригинал версии (дорожка C, documents.versions.original). */
export const VersionParamsSchema = z.object({ id: IdSchema, versionId: IdSchema });

/** queued — ждёт, parsing — идёт, parsed — разобран, failed — не удался (data-model.md). */
export const parseStates = ['queued', 'parsing', 'parsed', 'failed'] as const;
export type ParseState = (typeof parseStates)[number];
export const ParseStateSchema = z.enum(parseStates);

export const parseStateLabels = {
  queued: 'Ждёт разбора',
  parsing: 'Разбор идёт',
  parsed: 'Разобран',
  failed: 'Разбор не удался',
} as const satisfies Record<ParseState, string>;

/** Итог разбора (FR-031). */
export const ParseSummarySchema = z.object({
  items: z.number().int().min(0),
  open: z.number().int().min(0),
  clarify: z.number().int().min(0),
  /** Закрыто по свидетельству выполнения — решение ИИ не ниже порога (004, FR-022). */
  aiClosed: z.number().int().min(0).optional(),
  /** Свидетельство не проверено из-за отказа модели (004, FR-022). */
  evidenceUnchecked: z.number().int().min(0).optional(),
});
export type ParseSummary = z.infer<typeof ParseSummarySchema>;

export const DocumentVersionSchema = z.object({
  id: IdSchema,
  fileName: z.string(),
  sheetName: z.string(),
  sheetDate: DateSchema,
  schemaId: z.string(),
  /** Версия схемы, по которой лист разбирается (010); у версий до 010 — null. */
  schemaVersion: z.number().int().min(1).nullable().optional(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  parseState: ParseStateSchema,
  summary: ParseSummarySchema.nullable(),
  /** Код и номер операции, если разбор не удался (FR-030). */
  error: z.object({ code: ErrorCodeSchema, operation: OperationIdSchema }).nullable(),
  uploadedAt: TimestampSchema,
});
export type DocumentVersion = z.infer<typeof DocumentVersionSchema>;

/** Строка списка документов (FR-032). */
export const DocumentRowSchema = z.object({
  id: IdSchema,
  /** П-N. */
  number: z.number().int().min(1),
  title: z.string(),
  /** Последняя загруженная версия — в любом состоянии разбора; null — протокол без файла (014). */
  latest: DocumentVersionSchema.nullable(),
  /** Последняя разобранная версия — по ней таблица и лист контроля. */
  lastParsedVersionId: IdSchema.nullable(),
  /** Открытые пункты последней разобранной версии. */
  openItems: z.number().int().min(0),
  /** Режим протокола (011, ADR-0031): файл или ведётся в системе. */
  mode: DocumentModeSchema,
  /** Когда и кто перевёл протокол в систему; у режима файла — null. */
  maintainedAt: TimestampSchema.nullable(),
  maintainedBy: PersonRefSchema.nullable(),
  /**
   * Владелец протокола (041, FR-001, FR-005): строка access.protocol_owners; archived — владелец
   * в архиве («ФИО · в архиве»); нет владельца — null.
   */
  lead: z
    .object({ userId: IdSchema, name: z.string(), archived: z.boolean().default(false) })
    .nullable(),
});
export type DocumentRow = z.infer<typeof DocumentRowSchema>;

export const DocumentListSchema = z.object({ items: z.array(DocumentRowSchema) });

/** Версии документа, новые сверху — боковая панель документа (приёмка 0.3.1). */
export const DocumentVersionListSchema = z.object({ items: z.array(DocumentVersionSchema) });
export type DocumentVersionList = z.infer<typeof DocumentVersionListSchema>;

/** Лист файла в предпросмотре загрузки (FR-026). */
export const PreviewSheetSchema = z.object({
  name: z.string(),
  /** Дата листа: из заголовка протокола или названия; нет — null, лист не предлагается. */
  date: DateSchema.nullable(),
  /** Узнанная схема шаблона или null — шапка не узнана. */
  schemaId: z.string().nullable(),
  schemaTitle: z.string().nullable(),
  /** Лист — архив выполненных: не предлагается и не загружается, DOC-005 (010, FR-042, FR-045). */
  archived: z.boolean().optional(),
  /** Дата из названия листа и дата из заголовка протокола (010, FR-043). */
  nameDate: DateSchema.nullable().optional(),
  titleDate: DateSchema.nullable().optional(),
  /** Правило нашло шапку таблицы, а схема её не узнала (010, FR-040). */
  headerFound: z.boolean().optional(),
});

export const PreviewQuerySchema = z.object({
  /** Для «Новой версии»: сравнить с последней версией документа (FR-027). */
  documentId: IdSchema.optional(),
});

export const PreviewResponseSchema = z.object({
  sheets: z.array(PreviewSheetSchema),
  /** Предложенный лист: самый свежий с узнанной шапкой. */
  suggested: z.string().nullable(),
  /** Дата листа последней версии документа — для предупреждения «лист старше». */
  lastSheetDate: DateSchema.nullable(),
  /** Листов с шапкой, которую не узнала ни одна схема — окно предлагает мастер (010, FR-040). */
  otherHeaderSheets: z.number().int().min(0).optional(),
});
export type PreviewResponse = z.infer<typeof PreviewResponseSchema>;

export const UploadQuerySchema = z.object({
  folderId: IdSchema.nullable().optional(),
  sheet: z.string().min(1),
  /**
   * Подтверждение загрузки листа старше или равного загруженному (FR-027, DOC-004). Строка
   * запроса: «true»/«false» — stringbool, а не coerce: coerce.boolean превращает «false» в true.
   */
  confirmOlder: z.stringbool().optional(),
});

/** Дата отсчёта: по листу недели (по умолчанию) или сегодня (FR-017). */
export const asOfModes = ['sheet', 'today'] as const;
export const AsOfModeSchema = z.enum(asOfModes);
export type AsOfMode = (typeof asOfModes)[number];

export const ControlSheetQuerySchema = z.object({ asOf: AsOfModeSchema.default('sheet') });

/**
 * Группа пункта — как на листе контроля (control/policy/group.ts, controlSheetGroupOf); quiet —
 * открытый без цвета без переносов, на листе только числом (FR-021); markClosed — закрыт
 * отметкой выполнения: на листе контроля только числом в шапке (010, FR-048).
 */
export const controlItemGroups = [
  'overdue',
  'burning',
  'clarify',
  'transfers',
  'aiReview',
  'aiClosed',
  'quiet',
  'markClosed',
] as const;
export type ControlItemGroup = (typeof controlItemGroups)[number];
export const ControlItemGroupSchema = z.enum(controlItemGroups);

/**
 * Фильтр «Статус» таблицы «Контроль» по группам листа (приёмка 0.3.1): none — без цвета
 * (transfers и quiet). Те же значения — у ссылок полосы показателей «Главной».
 */
export const controlGroupFilters = ['overdue', 'burning', 'clarify', 'aiReview', 'none'] as const;
export type ControlGroupFilter = (typeof controlGroupFilters)[number];
export const ControlGroupFilterSchema = z.enum(controlGroupFilters);

/** Вид таблицы «Контроль»: открытые, закрытые ИИ (004), закрытые отметкой выполнения (010). */
export const controlViews = ['open', 'aiClosed', 'markClosed'] as const;
export type ControlView = (typeof controlViews)[number];

/** Порядок строк: source — как в протоколе (документ, строка листа); due — по сроку, без срока в конце. */
export const controlSorts = ['source', 'due'] as const;
export type ControlSort = (typeof controlSorts)[number];
export const CONTROL_SEARCH_MAX = 200;

export const ControlItemsQuerySchema = z.object({
  documentId: IdSchema.optional(),
  /**
   * Поиск без учёта регистра по тексту пункта, разделу, номеру, ответственному, примечанию
   * и тексту ячейки срока (приёмка 0.3.1; «Табель», панель таблицы).
   */
  q: z.string().trim().min(1).max(CONTROL_SEARCH_MAX).optional(),
  /** Группы листа через запятую: overdue,aiReview. Вместе со status — оба условия. */
  group: z
    .string()
    .transform((s) => s.split(',').filter(Boolean))
    .pipe(z.array(ControlGroupFilterSchema))
    .optional(),
  sort: z.enum(controlSorts).default('source'),
  /** Несколько статусов — через запятую: red,clarify. */
  status: z
    .string()
    .transform((s) => s.split(',').filter(Boolean))
    .pipe(z.array(ItemStatusSchema))
    .optional(),
  dueBefore: DateSchema.optional(),
  asOf: AsOfModeSchema.default('sheet'),
  /**
   * open — открытые пункты (003); aiClosed — закрытые по свидетельству ИИ (004, FR-010);
   * markClosed — закрытые отметкой выполнения, с бейджем «Закрыто · отметка» (010, FR-048).
   */
  view: z.enum(controlViews).default('open'),
  /** Пункты, где упомянута запись справочника — фильтр «Ответственный» (005, FR-011). */
  responsibleId: IdSchema.optional(),
});

/**
 * Строка таблицы открытых пунктов (FR-036). 014 (FR-004): строки протоколов в режиме ведения —
 * `itemId` и `documentMode: maintained`; `versionId`, `sheetName`, `sheetDate`, `row` — версии
 * перевода, у протокола без файла — номер документа, «Таблица протокола», дата отсчёта и
 * порядковый номер строки в дереве.
 */
export const ControlItemSchema = z.object({
  documentId: IdSchema,
  /** Пункт протокола в режиме ведения — переход в таблицу (014: FR-004). */
  itemId: IdSchema.optional(),
  documentMode: DocumentModeSchema.optional(),
  documentNumber: z.number().int().min(1),
  /** Название протокола — заголовок группы таблицы и шапка боковой панели (приёмка 0.3.1). */
  documentTitle: z.string(),
  versionId: IdSchema,
  /** Лист недели версии и его дата. */
  sheetName: z.string(),
  sheetDate: DateSchema,
  row: z.number().int().min(1),
  kind: ItemKindSchema,
  section: z.string(),
  number: z.string().nullable(),
  text: z.string(),
  due: DateSchema.nullable(),
  dueRaw: z.string().nullable(),
  transfers: z.number().int().min(0),
  responsible: z.string().nullable(),
  /** Откуда ответственный: строка, родительская строка или колонка «Подрядчик» (FR-008 003). */
  responsibleFrom: ResponsibleSourceSchema.nullable().optional(),
  /** Прежние сроки — переносы, по порядку (FR-005 003). */
  transferDates: z.array(DateSchema).optional(),
  status: ItemStatusSchema,
  /** Группа листа контроля на дату отсчёта. */
  group: ControlItemGroupSchema,
  overdue: z.boolean(),
  paused: z.boolean(),
  clarify: ClarifyReasonSchema.nullable(),
  /** Дата, на которую посчитан статус. */
  asOf: DateSchema,
  /** Текст примечания как в файле — основание решения ИИ (004, FR-008). */
  comment: z.string().nullable().optional(),
  /** Чем закрыт: evidence — свидетельство выполнения (004), mark — отметка выполнения (010). */
  closedBy: ClosedBySchema.nullable().optional(),
  /** Текст ячейки с отметкой выполнения — бейдж «Закрыто · отметка» (010, FR-048). */
  mark: z.string().nullable().optional(),
  /** Вероятность свидетельства, если модель спрашивали (004, FR-010). */
  evidenceProbability: z.number().min(0).max(1).nullable().optional(),
  /** Свидетельство неуверенное — «Проверить выполнение (ИИ)», подтверждает человек (004). */
  evidenceReview: z.boolean().optional(),
  /**
   * Упоминания ответственного и подрядчика с записью справочника и адресатом (005, FR-011,
   * FR-033); нет — справочник пункт ещё не связал.
   */
  responsibles: z.array(ControlResponsibleSchema).optional(),
  /**
   * Пункт без ответственного: адресат — основной куратор юрлица зоны ответственности документа
   * или причина (нет зоны, нет основного куратора) (005, FR-033).
   */
  zoneAddressee: AddresseeSchema.nullable().optional(),
});
export type ControlItem = z.infer<typeof ControlItemSchema>;

export const ControlItemsResponseSchema = z.object({ items: z.array(ControlItemSchema) });
